import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Copy, Check, Share2, QrCode } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { useI18n } from '../lib/i18n';

type Props = {
  open: boolean;
  onClose: () => void;
  shareSlug: string;
  /** 'duplicates' | 'missing' — para el copy del share sheet */
  mode: 'duplicates' | 'missing';
  displayName: string;
};

/**
 * Modal para compartir la colección pública:
 *  · QR code grande (escaneable con cámara desde otro celular)
 *  · Botón copiar link
 *  · Botón share nativo (WhatsApp / iMessage / AirDrop / …) via Web Share API
 */
export function ShareModal({ open, onClose, shareSlug, mode, displayName }: Props) {
  const { t, lang } = useI18n();
  const [copied, setCopied] = useState(false);

  const url = `${window.location.origin}/u/${shareSlug}`;

  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // sin clipboard API
    }
  }

  async function shareNative() {
    const title = mode === 'duplicates'
      ? (lang === 'es' ? 'Mis repes Panini 2026' : 'My Panini 2026 dupes')
      : (lang === 'es' ? 'Lo que me falta del Panini 2026' : 'What I need for Panini 2026');
    const text = lang === 'es'
      ? `${displayName} · ¿Te sirve alguna? Mirá mi lista:`
      : `${displayName} · Any of these useful to you? Check my list:`;
    try {
      if (navigator.share) {
        await navigator.share({ title, text, url });
      } else {
        await copy();
      }
    } catch {
      /* usuario canceló share sheet */
    }
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={onClose}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            onClick={(e) => e.stopPropagation()}
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.2 }}
            className="relative w-full max-w-sm rounded-3xl glass-strong border border-cyan/30 shadow-[0_0_40px_-10px_rgba(94,224,255,0.4)] p-6"
          >
            <button
              onClick={onClose}
              className="absolute top-3 right-3 h-8 w-8 grid place-items-center rounded-full text-haze hover:text-foam hover:bg-white/10 transition"
              aria-label="close"
            >
              <X size={18} />
            </button>

            <div className="text-center mb-4">
              <p className="text-2xs uppercase tracking-widest text-haze mb-1">
                {t('common.share')}
              </p>
              <h2 className="font-display font-extrabold text-2xl">
                {mode === 'duplicates'
                  ? (lang === 'es' ? 'Mis repes' : 'My dupes')
                  : (lang === 'es' ? 'Lo que me falta' : 'What I need')}
              </h2>
              <p className="mt-1 text-xs text-haze">
                {lang === 'es'
                  ? 'Escaneá el QR o compartí el link para intercambiar.'
                  : 'Scan the QR or share the link to trade.'}
              </p>
            </div>

            {/* QR code — blanco puro, alto contraste, ideal para escanear */}
            <div className="mx-auto mb-5 grid place-items-center rounded-2xl bg-white p-4 w-fit shadow-[0_0_24px_-8px_rgba(198,255,94,0.4)]">
              <QRCodeSVG
                value={url}
                size={200}
                bgColor="#ffffff"
                fgColor="#06112E"
                level="M"
              />
            </div>

            {/* Link copiable */}
            <button
              onClick={copy}
              className="w-full flex items-center gap-2 rounded-xl border border-line/60 bg-white/5 hover:bg-white/10 transition px-3 py-2.5 text-left mb-2"
            >
              <div className="flex-1 min-w-0 text-xs font-mono text-haze truncate">{url}</div>
              {copied ? (
                <Check size={16} className="text-lime shrink-0" />
              ) : (
                <Copy size={16} className="text-haze shrink-0" />
              )}
            </button>

            {/* Share nativo */}
            <button
              onClick={shareNative}
              className="w-full flex items-center justify-center gap-2 rounded-xl bg-lime text-ink font-semibold py-3 shadow-glow-lime hover:scale-[1.02] active:scale-100 transition"
            >
              <Share2 size={18} strokeWidth={2.5} />
              {lang === 'es' ? 'Compartir por…' : 'Share via…'}
            </button>

            <p className="mt-4 text-2xs text-center text-haze">
              <QrCode size={12} className="inline mb-0.5 mr-1" />
              {lang === 'es'
                ? 'La persona necesita cuenta Pannini para armar el intercambio.'
                : 'The other person needs a Pannini account to complete the trade.'}
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
